<template>
  <Loading v-if="loading" size="100px" />
  <DashboardLayout v-else>
    <template v-slot:title>
      <div class="d-flex flex-column flex-md-row justify-content-md-between align-items-md-center">
        <h1 class="text-left mb-2 mb-md-0">
          {{
            $store.state.racesStore.selectedRace
              ? $store.state.racesStore.selectedRace.short_name
              : ""
          }}
        </h1>
        <div v-if="$store.state.racesStore.selectedRace" class="d-flex flex-wrap">
          <button
            class="btn btn-primary mr-2 mb-2 mb-md-0"
            @click="exportRaceResults"
          >
            Export Results
          </button>
          <button
            class="btn btn-primary mb-2 mb-md-0"
            @click="exportRaceTrackers"
          >
            Export Trackers
          </button>
        </div>
      </div>
      <hr class="mt-2" />
    </template>

    <template v-slot:body>
      <SelectRace :races="availableRaces" @select-race="selectRace" />
      <div class="card-deck" v-if="!loading">
        <DashboardCard
          cardTitle="Riders"
          :numberOfItems="numberOfRiders"
          :itemStates="riderStates"
          :referenceStates="riderRefData"
        >
          <template slot="icon">
            <i class="fa fa-biking mr-1" />
          </template>
        </DashboardCard>

        <DashboardCard
          cardTitle="Trackers"
          :numberOfItems="numberOfTrackers"
          :itemStates="trackerStates"
          :referenceStates="trackerRefData"
        >
          <template slot="icon">
            <i class="fa fa-satellite mr-1" />
          </template>
        </DashboardCard>

        <DashboardCard
          cardTitle="Wallets"
          :numberOfItems="numberOfWallets"
          :customContent="true"
        >
          <template slot="icon">
            <i class="fa fa-wallet mr-1" />
          </template>
          <template slot="customContent">
            <div v-if="wallets && wallets.length > 0" class="wallet-list">
              <div 
                v-for="wallet in wallets" 
                :key="wallet.uuid" 
                class="wallet-item d-flex justify-content-between align-items-center mb-2"
              >
                <div class="wallet-info">
                  <span class="wallet-name">{{ wallet.name || 'Unnamed Wallet' }}</span>
                </div>
                <div class="wallet-actions d-flex align-items-center">
                  <span class="wallet-balance badge badge-primary mr-2">
                    £{{ (wallet.balance / 100).toFixed(2) }}
                  </span>
                  <button 
                    class="btn btn-sm btn-outline-primary"
                    @click="downloadWalletTransactions(wallet)"
                    title="Download transactions"
                  >
                    <i class="fa fa-download"></i>
                  </button>
                </div>
              </div>
            </div>
            <div v-else class="text-muted">
              No wallets found for this race
            </div>
          </template>
        </DashboardCard>
      </div>
    </template>
  </DashboardLayout>
</template>

<script>
import Loading from "@/components/renderingComponents/Loading";
import DashboardLayout from "@/components/layoutComponents/DashboardLayout";
import DashboardCard from "@/components/renderingComponents/DashboardCard";
import SelectRace from "@/components/renderingComponents/SelectRace";
import { riderData } from "@/reference/RiderReferenceData.js";
import { trackerData } from "@/reference/TrackerReferenceData.js";

export default {
  name: "RacePage",
  components: {
    Loading,
    DashboardLayout,
    DashboardCard,
    SelectRace,
  },
  data() {
    return {
      loading: false,
      riderStates: Object(),
      trackerStates: Object(),
      numberOfTrackers: 0,
      numberOfRiders: 0,
      wallets: [],
      numberOfWallets: 0,
    };
  },
  computed: {
    availableRaces() {
      return this.$store.state.racesStore.availableRaces;
    },
    trackerRefData() {
      return trackerData.trackerStates;
    },
    riderRefData() {
      return riderData.riderStates;
    },
  },
  methods: {
    selectRace(race) {
      this.$store.commit("racesStore/setSelectedRace", race);
    },
    setRidersTrackers(selectedRace) {
      this.selectedRace = selectedRace;
      this.riderStates = selectedRace.rider_states;
      this.trackerStates = selectedRace.tracker_states;
      this.numberOfRiders = selectedRace.total_riders;
      this.numberOfTrackers = selectedRace.total_trackers;
      this.wallets = selectedRace.wallets || [];
      this.numberOfWallets = this.wallets.length;
    },
    exportRaceResults() {
      if (this.$store.state.racesStore.selectedRace) {
        this.$store.dispatch("racesStore/exportResults", this.$store.state.racesStore.selectedRace.uuid)
          .then(response => {
            const url = window.URL.createObjectURL(new Blob([response], { type: 'text/csv' }));
            const link = document.createElement('a');
            link.href = url;
            const fileName = `${this.$store.state.racesStore.selectedRace.short_name}-results.csv`;
            link.setAttribute('download', fileName);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
          })
          .catch(error => {
            console.error("Error exporting results:", error);
          });
      }
    },
    exportRaceTrackers() {
      if (this.$store.state.racesStore.selectedRace) {
        this.$store.dispatch("racesStore/exportTrackers", this.$store.state.racesStore.selectedRace.uuid)
          .then(response => {
            const url = window.URL.createObjectURL(new Blob([response], { type: 'text/csv' }));
            const link = document.createElement('a');
            link.href = url;
            const fileName = `${this.$store.state.racesStore.selectedRace.short_name}-trackers.csv`;
            link.setAttribute('download', fileName);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
          })
          .catch(error => {
            console.error("Error exporting trackers:", error);
          });
      }
    },
    downloadWalletTransactions(wallet) {
      this.$store.dispatch("racesStore/exportWalletTransactions", wallet.uuid)
        .then(response => {
          const url = window.URL.createObjectURL(new Blob([response], { type: 'text/csv' }));
          const link = document.createElement('a');
          link.href = url;
          
          // Generate filename with date and time
          const now = new Date();
          const dateStr = now.toLocaleDateString('en-GB').replace(/\//g, '-');
          const timeStr = now.toLocaleTimeString('en-GB', { hour12: false }).replace(/:/g, '-');
          const walletName = wallet.name || `wallet-${wallet.uuid}`;
          const fileName = `${walletName}-transactions-${dateStr}_${timeStr}.csv`;
          
          link.setAttribute('download', fileName);
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        })
        .catch(error => {
          console.error("Error exporting wallet transactions:", error);
        });
    },
  },
  created() {
    this.$store.dispatch("usersStore/getMe").then((user) => {
      this.$store.commit("usersStore/setMe", user);
      this.$store.commit("racesStore/setAvailableRaces", user.races);
      const previouslySelectedUuid = this.$store.state.racesStore.selectedRace?.uuid;
      const race =
        user.races.find((r) => r.uuid === previouslySelectedUuid) || user.races[0];
      this.$store.commit("racesStore/setSelectedRace", race);
      this.setRidersTrackers(race);
    });

    this.$watch(
      () => this.$store.state.racesStore.selectedRace,
      (selectedRace) => {
        this.setRidersTrackers(selectedRace);
      }
    );
  },
};
</script>

<style scoped>
.wallet-item {
  padding: 0.5rem 0;
  border-bottom: 1px solid #eee;
}

.wallet-item:last-child {
  border-bottom: none;
}

.wallet-name {
  font-weight: 500;
}

.wallet-balance {
  font-size: 0.9rem;
  font-weight: 600;
}

.wallet-actions {
  gap: 0.5rem;
}

.wallet-actions .btn {
  min-width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
}
</style>
